import { QueryClient, QueryClientProvider, queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";

import appCss from "../styles.css?url";
import { Toaster } from "@/components/ui/sonner";
import { LanguageProvider } from "@/lib/i18n";
import { getContentBundle, getMediaBundle, getLocales, getFavicon, type FaviconLink } from "@/lib/content.functions";
import { ThemeInjector, themeTokensQuery } from "@/components/ThemeInjector";
import { CookieBanner } from "@/components/CookieBanner";

const contentBundleQuery = queryOptions({
  queryKey: ["cms", "content"],
  queryFn: () => getContentBundle(),
  staleTime: 60_000,
});

const faviconQuery = queryOptions({
  queryKey: ["cms", "favicon"],
  queryFn: () => getFavicon(),
  staleTime: 60_000,
});

const FALLBACK_ICON_LINKS = [
  { rel: "icon", type: "image/x-icon", href: "/favicon.ico" },
  { rel: "icon", type: "image/png", sizes: "32x32", href: "/favicon-32x32.png" },
  { rel: "icon", type: "image/png", sizes: "16x16", href: "/favicon-16x16.png" },
  { rel: "apple-touch-icon", sizes: "180x180", href: "/apple-touch-icon.png" },
];

const localesQuery = queryOptions({
  queryKey: ["cms", "locales"],
  queryFn: () => getLocales(),
  staleTime: 60_000,
});

const mediaBundleQuery = queryOptions({
  queryKey: ["cms", "media"],
  queryFn: () => getMediaBundle(),
  staleTime: 60_000,
});


function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: ({ loaderData }) => {
    const apiLinks =
      (loaderData as { faviconLinks?: FaviconLink[] } | undefined)?.faviconLinks ?? [];
    const iconLinks =
      apiLinks.length > 0
        ? apiLinks.map((l: FaviconLink) => ({
            rel: l.rel,
            href: l.href,
            ...(l.sizes ? { sizes: l.sizes } : {}),
            ...(l.type ? { type: l.type } : {}),
          }))
        : FALLBACK_ICON_LINKS;
    return {
      meta: [
        { charSet: "utf-8" },
        { name: "viewport", content: "width=device-width, initial-scale=1" },
        { title: "PULSO33 — ¡Cuenta con nosotros!" },
        {
          name: "description",
          content:
            "PULSO33 builds AI agents, web applications, infrastructure, automation and BI. Real engineers shipping production-grade systems.",
        },
        { name: "author", content: "PULSO33" },
        { property: "og:title", content: "PULSO33 — ¡Cuenta con nosotros!" },
        {
          property: "og:description",
          content:
            "End-to-end IT services: AI agents, web applications, infrastructure, automation and BI. We build, deploy, manage and support.",
        },
        { property: "og:site_name", content: "PULSO33" },
        { property: "og:type", content: "website" },
        { name: "twitter:card", content: "summary" },
        { name: "twitter:title", content: "PULSO33 — ¡Cuenta con nosotros!" },
        { name: "description", content: "Soluciones tecnológicas completas. Web, IA, desarrollo a medida, infraestructura — Lo que necesites para hacer crecer tu negocio." },
        { property: "og:description", content: "Soluciones tecnológicas completas. Web, IA, desarrollo a medida, infraestructura — Lo que necesites para hacer crecer tu negocio." },
        { name: "twitter:description", content: "Soluciones tecnológicas completas. Web, IA, desarrollo a medida, infraestructura — Lo que necesites para hacer crecer tu negocio." },
        { property: "og:image", content: "https://storage.googleapis.com/gpt-engineer-file-uploads/P68hIbarargqEZkliScQjLSc0D42/social-images/social-1780970696026-logo-web.webp" },
        { name: "twitter:image", content: "https://storage.googleapis.com/gpt-engineer-file-uploads/P68hIbarargqEZkliScQjLSc0D42/social-images/social-1780970696026-logo-web.webp" },
      ],
      links: [
        { rel: "preconnect", href: "https://fonts.googleapis.com" },
        { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" } as never,
        { rel: "stylesheet", href: "https://fonts.googleapis.com/css2?family=Saira+Stencil+One&family=Inter:wght@400;500;600;700;800&family=JetBrains+Mono:wght@400;500;700&display=swap" },
        { rel: "stylesheet", href: appCss },
        ...iconLinks,
      ],
      scripts: [
        {
          children:
            "window.addEventListener('load',function(){var l=document.createElement('link');l.rel='manifest';l.href='/site.webmanifest';document.head.appendChild(l);});",
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "PULSO33",
            url: "https://pulso33.com",
            description:
              "End-to-end IT services: AI agents, web applications, infrastructure, automation and BI.",
            email: "info@pulso33.com",
          }),
        },
      ],
    };
  },
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
  loader: async ({ context }) => {
    const [favicon] = await Promise.all([
      context.queryClient.ensureQueryData(faviconQuery),
      context.queryClient.ensureQueryData(contentBundleQuery),
      context.queryClient.ensureQueryData(mediaBundleQuery),
      context.queryClient.ensureQueryData(themeTokensQuery),
      context.queryClient.ensureQueryData(localesQuery),
    ]);
    return { faviconLinks: favicon.links };
  },
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const { data: content } = useSuspenseQuery(contentBundleQuery, queryClient);
  const { data: media } = useSuspenseQuery(mediaBundleQuery, queryClient);
  const { data: localesData } = useSuspenseQuery(localesQuery, queryClient);

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeInjector />
      <LanguageProvider content={content.blocks} mediaBundle={media.assets} availableLocales={localesData.locales}>
        <Outlet />
        <Toaster />
        <CookieBanner />
      </LanguageProvider>
    </QueryClientProvider>
  );
}
